import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { sparkAuthTypes } from 'app/model/linkedservice/spark.linkedservice';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { enableSslWithTUAARadioSwitch, enableSslSimplified } from 'app/registries/linked-service/linked-service-shared-fields';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';

const usernameControl: ControlMetadata = {
    type: 'textbox',
    bind: 'username',
    label: stringResources.ModelName_UserName,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

const passwordControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'password',
        label: stringResources.ModelName_Password,
        required: true,
        isLabelVertical: true
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Spark_DisplayText),
        stringResources.ModelName_Password)
};

function authTypeControl(thriftProtocolType: string, defaultAuthType: string, version: string): ControlMetadata {
    return {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        defaultValue: defaultAuthType,
        options: [
            {
                // SASL does not support Anonymous authentication
                label: stringResources.Anonymous_DisplayText,
                value: sparkAuthTypes.Anonymous,
                hidden: thriftProtocolType === 'SASL'
            },
            {
                label: stringResources.UsernameLabel,
                value: sparkAuthTypes.Username,
                hidden: version === driverVersions.v2
            },
            {
                label: stringResources.AuthenticationType_UsernameAndPassword,
                value: sparkAuthTypes.UsernameAndPassword,
                hidden: thriftProtocolType === 'Binary'
            },
            {
                // Binary and SASL does not support Windows Azure HDInsight Service authentication
                label: stringResources.AuthenticationType_WindowsAzureHDInsightService,
                value: sparkAuthTypes.WindowsAzureHDInsightService,
                hidden: thriftProtocolType !== 'HTTP'
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        cases: getAuthTypeCases(version)
    };
}

function getAuthTypeCases(version: string): ControlMetadata['cases'] {
    return [
        {
            caseValue: sparkAuthTypes.Anonymous,
            children: [httpPathControl(sparkAuthTypes.Anonymous, version)]
        },
        {
            caseValue: sparkAuthTypes.Username,
            children: version === driverVersions.v2 ? [] : [usernameControl, httpPathControl(sparkAuthTypes.Username, version)]
        },
        {
            caseValue: sparkAuthTypes.UsernameAndPassword,
            children: [usernameControl, passwordControl, httpPathControl(sparkAuthTypes.UsernameAndPassword, version)]
        },
        {
            caseValue: sparkAuthTypes.WindowsAzureHDInsightService,
            children: [usernameControl, passwordControl, httpPathControl(sparkAuthTypes.WindowsAzureHDInsightService, version)]
        }
    ];
}

function httpPathControl(authType: string, version: string): ControlMetadata {
    return {
        type: 'textbox',
        bind: 'httpPath',
        label: stringResources.ModelName_HttpPath,
        isLabelVertical: true,
        defaultValue: authType === sparkAuthTypes.WindowsAzureHDInsightService && version === driverVersions.v2 ? '/sparkhive2' : undefined
    }
}

function connectionForm(version: string): ControlMetadata[] {
    const includeBinaryAndSasl = version !== driverVersions.v2 || GlobalVariables.enableSparkV2SaslAndBinary;

    return [
        {
            type: 'textbox',
            bind: 'host',
            label: stringResources.ModelName_Host,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'port',
            label: stringResources.ModelName_Port,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            inputType: 'number',
            defaultValue: 443,
            min: 0
        },
        {
            type: 'select',
            bind: 'serverType',
            label: stringResources.ModelName_ServerType,
            defaultValue: version === driverVersions.v2 ? 'SparkThriftServer' : undefined,
            isLabelVertical: true,
            options: version === driverVersions.v2 ? [
                {
                    label: stringResources.SparkThriftServer_DisplayText,
                    value: 'SparkThriftServer'
                }
            ] :
                [
                    {
                        label: stringResources.SharkServer_DisplayText,
                        value: 'SharkServer',
                    },
                    {
                        label: stringResources.SharkServer2_DisplayText,
                        value: 'SharkServer2',
                    },
                    {
                        label: stringResources.SparkThriftServer_DisplayText,
                        value: 'SparkThriftServer'
                    }
                ]
        },
        {
            type: 'select-switch',
            bind: 'thriftTransportProtocol',
            label: stringResources.ModelName_ThriftTransportProtocol,
            defaultValue: version === driverVersions.v2 ? 'HTTP' : undefined,
            isLabelVertical: true,
            options: includeBinaryAndSasl
                ? [
                    {
                        label: stringResources.ModelName_BinaryDataset,
                        value: 'Binary'
                    },
                    {
                        label: stringResources.SASL_displayName,
                        value: 'SASL'
                    },
                    {
                        label: stringResources.HttpServer_DisplayText,
                        value: 'HTTP'
                    }
                ]
                : [
                    {
                        label: stringResources.HttpServer_DisplayText,
                        value: 'HTTP'
                    }
                ],
            cases: includeBinaryAndSasl
                ? [
                    {
                        caseValue: 'Binary',
                        children: version === driverVersions.v2 ? [authTypeControl('Binary', sparkAuthTypes.Anonymous, driverVersions.v2)] : []
                    },
                    {
                        caseValue: 'SASL',
                        children: version === driverVersions.v2 ? [authTypeControl('SASL', sparkAuthTypes.UsernameAndPassword, driverVersions.v2)] : []
                    },
                    {
                        caseValue: 'HTTP',
                        children: version === driverVersions.v2 ? [authTypeControl('HTTP', sparkAuthTypes.Anonymous, driverVersions.v2)] : []
                    }
                ]
                : [
                    {
                        caseValue: 'HTTP',
                        children: version === driverVersions.v2 ? [authTypeControl('HTTP', sparkAuthTypes.Anonymous, driverVersions.v2)] : []
                    }
                ]
        },
        {
            type: 'select-switch',
            bind: 'authenticationType',
            label: stringResources.ModelName_AuthenticationType,
            // auth type depends on thriftTransportProtocol in V2
            hide: version === driverVersions.v2,
            options: version === driverVersions.v2 ? [
                {
                    label: stringResources.Anonymous_DisplayText,
                    value: sparkAuthTypes.Anonymous
                },

                {
                    label: stringResources.AuthenticationType_UsernameAndPassword,
                    value: sparkAuthTypes.UsernameAndPassword
                },
                {
                    label: stringResources.AuthenticationType_WindowsAzureHDInsightService,
                    value: sparkAuthTypes.WindowsAzureHDInsightService
                }
            ] :
                [
                    {
                        label: stringResources.Anonymous_DisplayText,
                        value: sparkAuthTypes.Anonymous
                    },
                    {
                        label: stringResources.UsernameLabel,
                        value: sparkAuthTypes.Username
                    },
                    {
                        label: stringResources.AuthenticationType_UsernameAndPassword,
                        value: sparkAuthTypes.UsernameAndPassword
                    },
                    {
                        label: stringResources.AuthenticationType_WindowsAzureHDInsightService,
                        value: sparkAuthTypes.WindowsAzureHDInsightService
                    }
                ],
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: sparkAuthTypes.Anonymous,
            cases: getAuthTypeCases(version)
        },
        version === driverVersions.v2 ? enableSslSimplified : enableSslWithTUAARadioSwitch
    ]
}

export const sparkMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.spark.linkedServiceType,
    displayName: stringResources.Spark_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-Spark'
    },
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-spark',
    isFirstClass: true,
    category: ['Database'],
    supportDatasetV2: true,
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    supportedInTrident: true,
    supportOPDG: GlobalVariables.enableOPDG,
    supportAutoFillCopilotInput: GlobalVariables.tridentBatch1ConnectorPhase2,
    isKnownInPipelineContext: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportCopyJobSource: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: ResourceKind.Spark
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.spark.copySourceType],
    SHIRVersion: {
        v2: '5.50.0.0'
    },
    controls:
        [
            {
                type: 'radio-switch',
                bind: 'version',
                label: stringResources.VersionLabel,
                isLabelVertical: true,
                required: true,
                options: driverVersionVersionOptions,
                hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
                defaultValue: driverVersions.v2,
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [
                            {
                                type: 'hint',
                                hintMode: 'warning',
                                description: format(
                                    stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                    stringResources.VersionLabel.toLocaleLowerCase()),
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-spark'
                            },
                            ...connectionForm(driverVersions.v1)
                        ]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: [...connectionForm(driverVersions.v2)]
                    }
                ]
            },
            ...(connectionForm(driverVersions.v2)
                        .map((control) => ({
                            ...control,
                            hide: combineHide(control.hide)
                        }))
                    )
        ]
};

function combineHide(origHide: boolean | string | (() => boolean) | undefined): boolean | string | (() => boolean) {
    const extraHide = !GlobalVariables.showLegacy ? '{version !== "v2" }' : true;
    return origHide || extraHide;
}
